import { Dialog } from "construct-ui";
import moment from "moment";
import textInputComponent from "../../components/common/text-input.component";
import DatePickerComponent from "../../components/date-picker/date-picker.component";
import MultiSelectComponent from "../../components/multi-select/multi-select.component";
import NumberInputComponent from "../../components/number/number-input.component";
import RatingInputComponent from "../../components/rating/rating-input.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import SwitchInputComponent from "../../components/switch/switch-input.component";
import { formats, SURVEY_QUESTION_TYPE_CODES } from "../../config/constants";
import surveyModel from "../../models/survey/survey.model";
import ViewBase from "../view-base.view";

/**
 * Survey Response Edit Dialog
 * 
 * Response display dialog that relies on inputs from external component.
 * Selecting parent question will automatically display the children questions.
 * 
 * @author Darrus Goh
 */
export default function SurveyResponseEditDialog() {
    ViewBase.call(this);
    const SURVEY_RESPONSE_DIALOG_FORM_ID = 'surveyResponseEditDialogForm';

    let questions = {};
    let optionChildrenMap = {};
    let displayedQuestionIds = [];
    let selectedQuestionId = 0;
    let beforeEditDisplayedQuestionIds = [];
    let beforeEditAllResponses = {};
    let allResponses = {};
    let userResponseDetails = {};
    let questionOptions = {};

    const onSubmit = (onSave) => (e) => {
        // Form validation
        if (!this.validateForm(e)) {
            return false;
        }

        try {
            // Only retrieve responses that are displayed
            let editedResponses = getDisplayedResponses();
            if (isEdited(editedResponses)) {
                const finalResponses = getFinalResponses(editedResponses);

                this.showLoading();
                surveyModel.updateResponses({ 'survey': finalResponses })
                    .then(() => {
                        this.showMessage(this.getText('EM00000762', 'Changes have been saved.'), this.getText('EM0000004', 'Success'), () => { onSave() });
                    })
                    .catch((error) => {
                        this.handleError(error);
                    })
                    .finally(() => { this.hideLoading() });

            } else {
                this.showErrorMessage(this.getText('EM00000755', 'No changes were made.'), this.getText('LT00000737', 'No Changes'));
            }
        }
        catch (error) {
            this.handleError(error);
        }
    }

    const onClose = (onCloseEvent) => () => {
        try {
            let finalizedResponses = getDisplayedResponses();
            if (isEdited(finalizedResponses)) {
                this.showConfirm(this.getText('EM00000754', 'There are unsaved changes, are you sure you want to close?'), this.getText('LT00000021', 'Close'), (action) => { action === 'YES' && onCloseEvent() });
            } else {
                onCloseEvent();
            }
        }
        catch (error) {
            this.handleError(error);
        }
    }

    const getFinalResponses = (editedResponses) => {
        let finalResponses = structuredClone(beforeEditAllResponses);

        // Removed before edit displayed questions
        for (const questionId of beforeEditDisplayedQuestionIds) {
            delete finalResponses[questionId];
        }

        finalResponses = Object.assign(finalResponses, editedResponses);

        let result = [];
        for (const questionId in finalResponses) {
            let responseTemplate = {
                surveyId: userResponseDetails.surveyId,
                surveyDate: userResponseDetails.surveyYMD,
                siteId: userResponseDetails.siteId,
                prmtId: userResponseDetails.promoterId,
                surveyTopicId: userResponseDetails.topicId.toString(),
                surveyQuestionId: questionId,
                surveyOptionId: '0',
                responseText: ''
            };

            // is multiselect
            if (Array.isArray(finalResponses[questionId])) {
                for (const option of finalResponses[questionId]) {
                    result.push(Object.assign({}, responseTemplate, {
                        surveyOptionId: questionOptions[questionId][option.code].OptionId,
                        responseText: option.name
                    }));
                }
            } else if (typeof finalResponses[questionId] === 'object') { // is single select
                const option = finalResponses[questionId];
                responseTemplate.surveyOptionId = questionOptions[questionId][option.code].OptionId;
                responseTemplate.responseText = option.name;
                result.push(responseTemplate)
            } else { // everything else
                responseTemplate.responseText = finalResponses[questionId];

                // Response only accepts string
                responseTemplate.responseText = responseTemplate.responseText.toString();

                result.push(responseTemplate);
            }
        }

        return result;
    }

    const getDisplayedResponses = () => {
        let result = {};
        for (const questionId of displayedQuestionIds) {
            let response = allResponses[questionId];

            if (response) {
                result[questionId] = response;
            }
        }
        return result;
    }

    const isEdited = (editedResponses) => {
        for (const questionId in editedResponses) {
            const editedResponse = editedResponses[questionId];
            const beforeEditResponse = beforeEditAllResponses[questionId];

            if (JSON.stringify(editedResponse) !== JSON.stringify(beforeEditResponse)) {
                return true;
            }
        }

        return false;
    };

    const generateOptionChildrenMap = (questions) => {
        let result = {};
        for (const key in questions) {
            let parentOptionId = questions[key].parentOptionId;

            // Ensure that question has parent
            // 0 = No Parent
            if (parentOptionId > 0) {
                if (!result.hasOwnProperty(parentOptionId)) {
                    result[parentOptionId] = [];
                }
                result[parentOptionId].push(key);
            }
        }
        return result;
    };

    const getResponseDetails = (responseParams) => {
        return surveyModel.getResponseDetails(responseParams)
            .then((result) => {
                allResponses = generateResponses(result.responseDetails);
                beforeEditAllResponses = structuredClone(allResponses);
                beforeEditDisplayedQuestionIds = getQuestionIdRecursive(selectedQuestionId);
            })
            .catch((error) => {
                this.handleError(error);
            });
    }

    const generateResponses = (responseDetails) => {
        let result = {};
        for (const item of responseDetails) {
            // Response has options
            if (item.surveyOptionId !== 0) {
                let obj = { code: item.optionKey, name: item.response };

                // Response is multi select
                if (item.questionType === SURVEY_QUESTION_TYPE_CODES.MULTIPLE || item.questionType === SURVEY_QUESTION_TYPE_CODES.MULTI_DROPDOWN) {

                    if (!result[item.questionId]) {
                        result[item.questionId] = [];
                    }

                    result[item.questionId].push(obj);
                } else { // Response is single select
                    result[item.questionId] = obj;
                }
            } else { // Response does not have options
                result[item.questionId] = item.response;
            }
        }
        return result;
    }

    const renderQuestions = (questionId) => {
        let result = [];
        displayedQuestionIds = getQuestionIdRecursive(questionId);

        for (const id of displayedQuestionIds) {
            result.push(getQuestion(questions[id], allResponses[id]));
        }
        return result;
    }

    const getQuestionIdRecursive = (questionId) => {
        const response = allResponses[questionId];
        let result = [questionId];
        let childQuestionIds = [];

        if (_.isEmpty(response)) {
            return result;
        }

        if (Array.isArray(response)) {
            for (const option of response) {
                let questionIds = optionChildrenMap[option.code];
                if (questionIds) {
                    childQuestionIds = childQuestionIds.concat(questionIds);
                }
            }
        } else if (typeof response === 'object') {
            childQuestionIds = optionChildrenMap[response.code];
        } else {
            return result;
        }

        if (!_.isEmpty(childQuestionIds)) {
            for (const childId of childQuestionIds) {
                result = result.concat(getQuestionIdRecursive(childId));
            }
        }

        return result;
    }

    const getQuestion = (questionDetails, response) => {
        let required = questionDetails.mandatoryYN === 'Y';

        return m('.row.mb-3',
            m('.col-12.mb-2',
                m(`label.col-form-label.d-block.p-0${required ? '.mandatory' : ''}`, `${questionDetails.questionText} (${questionDetails.questionId})`),
                m('label.col-form-label-sm.d-block.p-0.text-muted', questionDetails.questionType),
            ),
            m('.col-12.position-relative',
                getInputComponent(questionDetails, response, (e) => { allResponses[questionDetails.surveyQuestionId] = e; }),
                m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
            ),
        )
    };

    const getInputComponent = (questionDetails, value, event) => {
        // Prepare options for dropdown usage
        let dropdownOptions = [];
        if (!_.isEmpty(questionDetails.optionList)) {
            dropdownOptions = questionDetails.optionList.map(option => { return { code: option.optionId, name: option.optionText } });
        }

        let required = questionDetails.mandatoryYN === 'Y';

        switch (questionDetails.questionTypeCode) {
            case SURVEY_QUESTION_TYPE_CODES.TEXT:
                return m('input', {
                    class: 'form-control form-control-bg',
                    placeholder: questionDetails.questionHint,
                    required,
                    type: 'text',
                    value,
                    onchange: (e) => { event(e.target.value) }
                });
            case SURVEY_QUESTION_TYPE_CODES.SINGLE_DROPDOWN:
            case SURVEY_QUESTION_TYPE_CODES.SINGLE:
                return m(SingleSelectComponent, {
                    required,
                    items: dropdownOptions,
                    selectedItem: value,
                    onSelect: event
                })
            case SURVEY_QUESTION_TYPE_CODES.MULTI_DROPDOWN:
            case SURVEY_QUESTION_TYPE_CODES.MULTIPLE:
                return m(MultiSelectComponent, {
                    required,
                    items: dropdownOptions,
                    selectedItems: value,
                    onSelect: event
                });
            case SURVEY_QUESTION_TYPE_CODES.NUMBER:
                return m(NumberInputComponent, {
                    required,
                    placeholder: questionDetails.questionHint,
                    min: questionDetails.minValue,
                    max: questionDetails.maxValue,
                    step: '.001',
                    value,
                    onChange: event
                });
            case SURVEY_QUESTION_TYPE_CODES.DATE:
                return m(DatePickerComponent, {
                    class: 'form-control',
                    required,
                    placeholder: questionDetails.questionHint,
                    date: value ? moment(value).format(formats.dateFormat) : value,
                    onselect: (e) => { 
                        const newDate = e.detail.date;
                        if(newDate) {
                            event(moment(newDate).format(formats.apiDateFormat)) 
                        } else {
                            event(newDate);
                        }
                    }
                });
            case SURVEY_QUESTION_TYPE_CODES.YES_NO: {
                const switchFlag = value === 'Y';

                // required attribute is not passed in as false is a valid answer
                return m(SwitchInputComponent, {
                    value: switchFlag,
                    label: switchFlag ? this.getText('LT00000023', 'Yes') : this.getText('LT00000024', 'No'),
                    onchange: (result) => { event(result ? 'Y' : 'N'); }
                })
            }
            case SURVEY_QUESTION_TYPE_CODES.RATING:
                return m(RatingInputComponent, {
                    value,
                    required,
                    size: 5,
                    minText: questionDetails.ratingWordsMin,
                    maxText: questionDetails.ratingWordsMax,
                    onchange: event
                });
            default:
                return m('input', {
                    class: 'form-control form-control-bg',
                    type: 'text',
                    disabled: true,
                    readonly: true,
                    placeholder: this.getText('EM00000761', 'Unsupported input type.')
                });
        }
    };

    return {
        oninit: ({ attrs }) => {
            questions = attrs.surveyQuestions;
            questionOptions = attrs.questionOptions;
            selectedQuestionId = attrs.selectedQuestionId;
            optionChildrenMap = generateOptionChildrenMap(questions);
            userResponseDetails = {
                surveyId: attrs.encryptedSurveyId,
                surveyYMD: attrs.selectedResponse.surveyYMD,
                site: attrs.selectedResponse.site,
                siteId: attrs.selectedResponse.siteCode,
                promoterId: attrs.selectedResponse.promoterId,
                topicId: attrs.selectedResponse.topicId
            };

            this.showLoading();
            getResponseDetails(userResponseDetails)
                .finally(() => { this.hideLoading() });
        },
        view: ({ attrs }) => {
            return [m(Dialog, {
                title: "Edit Response",
                class: 'cui-dialog-xl',
                isOpen: true,
                onClose: onClose(attrs.onClose),
                closeOnEscapeKey: false,
                closeOnOutsideClick: false,
                content: m('.container',
                    m('.card',
                        m('.card-body',
                            // Response Details
                            m('.row.mb-3',
                                m('.col-sm-12.col-md-4.mb-4.mb-md-0',
                                    m(textInputComponent, {
                                        label: this.getText("LT00000739", "Response Date"),
                                        value: moment(userResponseDetails.surveyYMD).format(formats.dateFormat),
                                        disabled: true,
                                        readonly: true
                                    })
                                ),
                                m('.col-sm-12.col-md-4.mb-4.mb-md-0',
                                    m(textInputComponent, {
                                        label: this.getText("LT00000071", "Site"),
                                        value: userResponseDetails.site,
                                        disabled: true,
                                        readonly: true
                                    })
                                ),
                                m('.col-sm-12.col-md-4',
                                    m(textInputComponent, {
                                        label: this.getText("LT00000009", "User"),
                                        value: userResponseDetails.promoterId,
                                        disabled: true,
                                        readonly: true
                                    })
                                )
                            ),

                            // Horizontal Rule
                            m('hr.mb-3'),

                            // Questions
                            m('form.needs-validation[novalidate]', {
                                id: SURVEY_RESPONSE_DIALOG_FORM_ID,
                                onsubmit: onSubmit(attrs.onSave)
                            },
                                renderQuestions(selectedQuestionId)
                            )
                        )
                    ),
                    this.getSpinner()
                ),
                footer: m('.col-md-12 .gap-2 .d-flex .justify-content-center',
                    m('button.btn.btn-primary', {
                        type: 'submit',
                        form: SURVEY_RESPONSE_DIALOG_FORM_ID
                    },
                        this.getText("LT00000022", "Save")
                    ),
                    m('button.btn.btn-white', {
                        type: 'button',
                        onclick: onClose(attrs.onClose)
                    },
                        this.getText("LT00000021", "Close")
                    )
                )
            }),
            this.getMessageComponent()];
        }
    }
}